/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { VocabularyItem } from '../../types/api';
import { vocabularyApi } from '../../sdk';
import AddVocabulary from './AddVocabulary';
import StudySession from './StudySession';
import VocabularyList from './VocabularyList';

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
    text-align: center;
`;

const Title = styled.h1`
    margin-bottom: 15px;
    text-align: center;
    color: #6b46c1;
    font-size: 2.5rem;
    text-shadow: 0 2px 4px rgba(107, 70, 193, 0.2);
`;

const Description = styled.div`
    margin-bottom: 40px;
    color: #7c3aed;
    text-align: center;
    font-size: 1.1rem;
    font-weight: 500;
`;

const Navigation = styled.nav`
    margin-bottom: 30px;
    display: flex;
    justify-content: center;
    gap: 15px;
    flex-wrap: wrap;
`;

const NavButton = styled.button<{ isActive?: boolean }>`
    padding: 12px 20px;
    background: ${(props) =>
        props.isActive
            ? 'linear-gradient(135deg, #a855f7 0%, #8b5cf6 100%)'
            : 'rgba(255, 255, 255, 0.9)'};
    color: ${(props) => (props.isActive ? 'white' : '#7c3aed')};
    border: 2px solid ${(props) => (props.isActive ? '#8b5cf6' : '#d8b4fe')};
    border-radius: 12px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(168, 85, 247, 0.2);

    &:hover {
        background: ${(props) =>
            props.isActive
                ? 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)'
                : 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)'};
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(168, 85, 247, 0.3);
    }
`;

const StudyJapanese: React.FC = () => {
    const auth = useAuth();
    const history = useHistory();
    const location = useLocation();
    const [vocabularyData, setVocabularyData] = useState<VocabularyItem[]>([]);
    const [apiError, setApiError] = useState<string>('');

    // Determine current page from URL
    const getCurrentPage = (): 'add' | 'study' | 'list' => {
        if (location.pathname === '/study-japanese/add') return 'add';
        if (location.pathname === '/study-japanese/study') return 'study';
        if (location.pathname === '/study-japanese/list') return 'list';
        return 'add'; // default
    };

    const currentPage = getCurrentPage();

    const fetchVocabulary = useCallback(async () => {
        if (auth.user?.access_token) {
            try {
                const data = await vocabularyApi.getVocabulary(auth.user.access_token);
                setVocabularyData(data);
                setApiError(''); // Clear any previous errors
            } catch (error) {
                // Set error state for user feedback
                if (error instanceof Error) {
                    setApiError(`Request failed: ${error.message}`);
                } else {
                    setApiError('Unknown error occurred');
                }
            }
        }
    }, [auth.user?.access_token]);

    useEffect(() => {
        fetchVocabulary();
    }, [fetchVocabulary]);

    return (
        <Container>
            <Title>Study Japanese</Title>
            <Description>This page is to practise japanese.</Description>

            <Navigation>
                <NavButton
                    type="button"
                    isActive={currentPage === 'add'}
                    onClick={() => history.push('/study-japanese/add')}
                >
                    Add Vocabulary
                </NavButton>
                <NavButton
                    type="button"
                    isActive={currentPage === 'study'}
                    onClick={() => history.push('/study-japanese/study')}
                >
                    Study Session
                </NavButton>
                <NavButton
                    type="button"
                    isActive={currentPage === 'list'}
                    onClick={() => history.push('/study-japanese/list')}
                >
                    Vocabulary List
                </NavButton>
            </Navigation>

            {/* Conditional Page Rendering */}
            {currentPage === 'add' && <AddVocabulary onVocabularyAdded={fetchVocabulary} />}
            {currentPage === 'study' && <StudySession vocabularyData={vocabularyData} />}
            {currentPage === 'list' && (
                <VocabularyList
                    vocabularyData={vocabularyData}
                    apiError={apiError}
                    onRefresh={fetchVocabulary}
                />
            )}
        </Container>
    );
};

export default StudyJapanese;
